import { useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { IFilterListProps } from 'common/types';
import {
    LIST_PADDING,
    MAX_LIST_HEIGHT,
    ROW_HEIGHT,
} from 'common/constants/filterList';

import { FilterListRow, IFilterListRowData } from './FilterListRow';
import { FilterListInnerList } from './FilterListInnerList';

export const FilterList = ({
    items,
    value,
    onChange,
    'aria-label': ariaLabel,
}: IFilterListProps) => {
    const selected = useMemo(() => new Set(value), [value]);

    const handleToggle = useCallback(
        (itemValue: string) => {
            if (selected.has(itemValue)) {
                onChange(value.filter((v) => v !== itemValue));
            } else {
                onChange([...value, itemValue]);
            }
        },
        [value, selected, onChange]
    );

    const rowData: IFilterListRowData = useMemo(
        () => ({ items, selected, onToggle: handleToggle }),
        [items, selected, handleToggle]
    );

    const listHeight = Math.min(
        items.length * ROW_HEIGHT + LIST_PADDING * 2,
        MAX_LIST_HEIGHT
    );

    return (
        <Box sx={{ height: listHeight, width: '100%' }}>
            <AutoSizer disableHeight>
                {({ width }) => (
                    <FixedSizeList
                        aria-label={ariaLabel}
                        height={listHeight}
                        innerElementType={FilterListInnerList}
                        itemCount={items.length}
                        itemData={rowData}
                        itemSize={ROW_HEIGHT}
                        width={width}
                    >
                        {FilterListRow}
                    </FixedSizeList>
                )}
            </AutoSizer>
        </Box>
    );
};
